import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import { getFacilityHierarchyId } from "@utils/facilityUtils";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import type {
  CahDashboardData,
  CahDashboardFacility,
  NetworkDashboardMeta,
} from "@services/networkDashboard";
import {
  abbreviateFacilityType,
  facilityLocation,
  formatCcnLabel,
  networkRegistryDisplayIds,
} from "./facilityNetworkChildrenUtils";

export const CAH_TYPE_CODE = "CAH";

export const CAH_DASHBOARD_SUBTITLE =
  "MBQIP · operating margin · 25-bed cap · swing bed ADC";

export const CAH_ROSTER_SUBTITLE =
  "CAH-specific parameters · click a facility to view its dashboard";

export type CahTone = "error" | "warn" | "ok" | "muted" | "neutral";

export type CahKpiTile = {
  id: string;
  label: string;
  value: string;
  sublabel: string;
  valueTone?: CahTone;
};

export type CahRosterRow = {
  key: string;
  facilityId: number | null;
  name: string;
  metaLine: string;
  bedsLabel: string;
  marginLabel: string;
  marginTone: CahTone;
  swingAdcLabel: string;
  lastSurveyLabel: string;
  lastSurveyTone: CahTone;
  exclusionLabel: string | null;
  dashboardFacility: MyFacilityListRow | null;
};

export type CahDashboardModel = {
  facilityCount: number;
  kpis: CahKpiTile[];
  roster: CahRosterRow[];
};

function toDashboardFacility(
  facility: FacilityHierarchyInput,
  facilityId: number,
): MyFacilityListRow {
  return {
    facility_id: facilityId,
    id: facilityId,
    facility_name: facility.facility_name || `Facility ${facilityId}`,
    city: typeof facility.city === "string" ? facility.city : "",
    state: typeof facility.state === "string" ? facility.state : "",
    full_address: facilityLocation(facility),
    facility_type:
      (typeof facility.facility_type === "string" && facility.facility_type) || "CAH",
    facility_type_slug:
      (typeof facility.facility_type_slug === "string" && facility.facility_type_slug) ||
      "critical_access_hospital",
    org_id: 0,
    org_name: "",
  };
}

export function collectLinkedCaHFacilities(
  members: FacilityHierarchyInput[],
  selectedId: number | null,
): FacilityHierarchyInput[] {
  return members.filter((member) => {
    const id = getFacilityHierarchyId(member);
    if (selectedId != null && id === selectedId) return false;
    return abbreviateFacilityType(member) === CAH_TYPE_CODE;
  });
}

function buildMetaLine(facility: FacilityHierarchyInput): string {
  const ids = networkRegistryDisplayIds(facility);
  const ccn = ids.ccn ? formatCcnLabel(ids.ccn) : "";
  const location = facilityLocation(facility);
  return [ccn, location !== "—" ? location : ""].filter(Boolean).join(" · ");
}

function normalizeCcn(value: string | null | undefined): string {
  return String(value || "")
    .replace(/\D/g, "")
    .padStart(6, "0");
}

function linkedFacilityByCcn(
  linkedCaHs: FacilityHierarchyInput[],
): Map<string, FacilityHierarchyInput> {
  const result = new Map<string, FacilityHierarchyInput>();
  linkedCaHs.forEach((facility) => {
    const ccn = networkRegistryDisplayIds(facility).ccn;
    if (ccn) result.set(normalizeCcn(ccn), facility);
  });
  return result;
}

function toNumber(value: number | string | null | undefined): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value).replace(/%/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function formatBeds(count: number | null): string {
  if (count == null) return "—";
  return String(count);
}

function formatMargin(value: number | string | null | undefined): {
  label: string;
  tone: CahTone;
} {
  const n = toNumber(value);
  if (n == null) return { label: "—", tone: "muted" };
  // Aggregator may send fraction (0.082) or percent (8.2)
  const pct = Math.abs(n) <= 1 ? n * 100 : n;
  const label = `${pct >= 0 ? "" : "−"}${Math.abs(pct).toFixed(1)}%`;
  if (pct < 0) return { label, tone: "error" };
  if (pct < 2) return { label, tone: "warn" };
  return { label, tone: "ok" };
}

function formatSwingAdc(value: number | null): string {
  if (value == null) return "—";
  return value.toFixed(1);
}

function formatSurveyDate(value: string | null | undefined): {
  label: string;
  tone: CahTone;
} {
  if (!value) return { label: "—", tone: "muted" };
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return { label: value, tone: "muted" };
  const label = parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const daysAgo = Math.floor(
    (Date.now() - parsed.getTime()) / (1000 * 60 * 60 * 24),
  );
  // CAH surveys roughly every 36 months; flag older than ~30 months
  if (daysAgo > 900) return { label, tone: "error" };
  if (daysAgo > 730) return { label, tone: "warn" };
  return { label, tone: "muted" };
}

function rowName(
  row: CahDashboardFacility,
  linked: FacilityHierarchyInput | undefined,
  index: number,
): string {
  return (
    row.facility_name ||
    row.name ||
    linked?.facility_name ||
    `CAH ${index + 1}`
  );
}

function average(
  values: Array<number | null>,
): number | null {
  const nums = values.filter((v): v is number => v != null);
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

const POS_NOT_CAH = "POS does not classify this CCN as CAH";

function lookupMetaValue<T>(
  map: Record<string, T> | undefined,
  ccn: string,
): T | undefined {
  if (!map) return undefined;
  return map[ccn] ?? map[ccn.replace(/^0+/, "")] ?? map[ccn.padStart(6, "0")];
}

function facilitiesWithExcluded(
  data: CahDashboardData,
  meta?: NetworkDashboardMeta,
): Array<CahDashboardFacility & { exclusionLabel?: string | null }> {
  const rows: Array<CahDashboardFacility & { exclusionLabel?: string | null }> = [
    ...(data.facilities ?? []),
  ];
  const present = new Set(rows.map((row) => normalizeCcn(row.ccn)));
  for (const raw of meta?.excluded_ccns ?? []) {
    const ccn = normalizeCcn(raw);
    if (!ccn || present.has(ccn)) continue;
    present.add(ccn);
    const location = lookupMetaValue(meta?.ccn_facility_locations, ccn);
    rows.push({
      ccn,
      facility_id: lookupMetaValue(meta?.ccn_facility_ids, ccn) ?? null,
      name: lookupMetaValue(meta?.ccn_facility_names, ccn) ?? null,
      city: location?.city ?? null,
      state: location?.state ?? null,
      certified_bed_count: null,
      swing_bed_adc: null,
      operating_margin: null,
      last_standard_survey_date: null,
      exclusionLabel: POS_NOT_CAH,
    });
  }
  return rows;
}

export function buildLiveCaHDashboardModel(
  data: CahDashboardData,
  linkedCaHs: FacilityHierarchyInput[],
  meta?: NetworkDashboardMeta,
): CahDashboardModel {
  const sourceFacilities = facilitiesWithExcluded(data, meta);
  const linkedByCcn = linkedFacilityByCcn(linkedCaHs);
  const roster: CahRosterRow[] = sourceFacilities.map((row, index) => {
    const linked = linkedByCcn.get(normalizeCcn(row.ccn));
    const facilityId =
      (linked ? getFacilityHierarchyId(linked) : null) ??
      row.facility_id ??
      null;
    const beds = toNumber(row.certified_bed_count);
    const margin = formatMargin(row.operating_margin);
    const survey = formatSurveyDate(row.last_standard_survey_date);
    const swing = toNumber(row.swing_bed_adc);
    return {
      key: facilityId != null ? `cah-${facilityId}` : `cah-idx-${index}`,
      facilityId,
      name: rowName(row, linked, index),
      metaLine: [
        row.ccn ? formatCcnLabel(row.ccn) : linked ? buildMetaLine(linked) : "CCN —",
        [row.city, row.state].filter(Boolean).join(", ") || null,
      ]
        .filter(Boolean)
        .join(" · "),
      bedsLabel: formatBeds(beds),
      marginLabel: margin.label,
      marginTone: margin.tone,
      swingAdcLabel: formatSwingAdc(swing),
      lastSurveyLabel: survey.label,
      lastSurveyTone: survey.tone,
      exclusionLabel: row.exclusionLabel ?? null,
      dashboardFacility:
        linked && facilityId != null
          ? toDashboardFacility(linked, facilityId)
          : null,
    };
  });

  const summary = data.summary ?? {};
  const facilityCount = Math.max(
    data.total_count ?? summary.cah_count ?? 0,
    roster.length,
  );
  const avgBeds = average(
    data.facilities.map((f) => toNumber(f.certified_bed_count)),
  );
  const avgSwing = average(
    data.facilities.map((f) => toNumber(f.swing_bed_adc)),
  );
  const marginKpi = formatMargin(summary.weighted_network_operating_margin);
  const mbqipAvailable = summary.public_mbqip_available_count;
  const mbqipDomains = summary.public_mbqip_domain_count;

  const emptyKpis: CahKpiTile[] = [
    {
      id: "count",
      label: "Network CAHs",
      value: "—",
      sublabel: "No CAHs in portfolio",
    },
    {
      id: "margin",
      label: "Operating margin",
      value: "—",
      sublabel: "Weighted network margin",
    },
    {
      id: "mbqip",
      label: "MBQIP coverage",
      value: "—",
      sublabel: "Public quality domains available",
    },
    {
      id: "beds",
      label: "Avg certified beds",
      value: "—",
      sublabel: "Across portfolio CAHs",
    },
  ];

  const kpis: CahKpiTile[] =
    facilityCount === 0
      ? emptyKpis
      : [
          {
            id: "count",
            label: "Network CAHs",
            value: String(summary.cah_count ?? facilityCount),
            sublabel:
              avgSwing == null
                ? "Critical access hospitals in scope"
                : `Avg swing-bed ADC ${avgSwing.toFixed(1)}`,
          },
          {
            id: "margin",
            label: "Operating margin",
            value: marginKpi.label,
            sublabel: "Weighted network operating margin",
            valueTone: marginKpi.tone,
          },
          {
            id: "mbqip",
            label: "MBQIP coverage",
            value:
              mbqipAvailable == null && mbqipDomains == null
                ? "—"
                : `${mbqipAvailable ?? "—"} / ${mbqipDomains ?? "—"}`,
            sublabel: "Public MBQIP domains available",
          },
          {
            id: "beds",
            label: "Avg certified beds",
            value: avgBeds == null ? "—" : formatBeds(Math.round(avgBeds)),
            sublabel: "Across portfolio CAHs",
          },
        ];

  return { facilityCount, kpis, roster };
}

/** Empty / membership-only model before API load. */
export function buildCaHDashboardModel(
  linkedCaHs: FacilityHierarchyInput[],
): CahDashboardModel {
  return buildLiveCaHDashboardModel(
    {
      summary: {
        cah_count: linkedCaHs.length,
        public_mbqip_available_count: null,
        public_mbqip_domain_count: null,
        weighted_network_operating_margin: null,
      },
      facilities: linkedCaHs.map((facility) => ({
        ccn: networkRegistryDisplayIds(facility).ccn ?? null,
        facility_name: facility.facility_name ?? null,
        city: typeof facility.city === "string" ? facility.city : null,
        state: typeof facility.state === "string" ? facility.state : null,
        certified_bed_count: null,
        swing_bed_adc: null,
        operating_margin: null,
        last_standard_survey_date: null,
      })),
      total_count: linkedCaHs.length,
    },
    linkedCaHs,
  );
}

export function cahToneColor(tone: CahTone): string {
  if (tone === "error") return "#a4262c";
  if (tone === "warn") return "#8b4409";
  if (tone === "ok") return "#107c10";
  if (tone === "muted") return "#737373";
  return "#323130";
}

export function isCaHTypeDashboard(typeCode: string | null | undefined): boolean {
  return String(typeCode || "").toUpperCase() === CAH_TYPE_CODE;
}
